// External
import { useState, useCallback, useMemo, useEffect } from "react";
import _ from "lodash";

// Internal
import {
  buildListItemsMap,
  buildConfigurationFromListItems,
} from "../../../modules/utils";

export const useListItemsState = ({
  sectionViewModel,
  rowSectionViewModel,
  gradingPeriods,
  originalConfiguration,
  baseConfiguration,
  selectedGradeScaleView,
  initialListItems,
  initialIsDirty = false,
  selectedGradeId,
  onUpdatesChange,
  onListItemsChange,
}) => {
  const [hasUserChanges, setHasUserChanges] = useState(initialIsDirty);
  const [listItems, setListItems] = useState(
    () =>
      initialListItems ||
      buildListItemsMap({
        sections: [...sectionViewModel, ...rowSectionViewModel],
      })
  );

  const builtConfiguration = useMemo(
    () =>
      buildConfigurationFromListItems({
        listItems,
        columnSections: sectionViewModel,
        rowSections: rowSectionViewModel,
        gradingPeriods,
        baseConfiguration: baseConfiguration || originalConfiguration || {},
        selectedGradeScaleView,
        selectedGradeId,
      }),
    [
      baseConfiguration,
      originalConfiguration,
      gradingPeriods,
      listItems,
      rowSectionViewModel,
      selectedGradeId,
      selectedGradeScaleView,
      sectionViewModel,
    ]
  );

  useEffect(() => {
    const key = selectedGradeId || "DEFAULT";
    const currentConfiguration = _.omit(originalConfiguration || {}, [
      "isOverridden",
    ]);
    const hasConfigurationChanges = !_.isEqual(
      builtConfiguration,
      currentConfiguration
    );
    const isDirty = hasUserChanges && hasConfigurationChanges;

    if (onUpdatesChange) {
      onUpdatesChange({
        key,
        selectedGradeId: selectedGradeId || null,
        configuration: builtConfiguration,
        isDirty,
      });
    }

    if (onListItemsChange) {
      onListItemsChange({
        key,
        listItems,
      });
    }
  }, [
    hasUserChanges,
    builtConfiguration,
    listItems,
    onListItemsChange,
    onUpdatesChange,
    selectedGradeId,
    originalConfiguration,
  ]);

  const updateList = useCallback(({ listKey, updater, markDirty = true }) => {
    setListItems(prev => {
      const previousList = prev[listKey] || [];
      const nextList = updater(previousList);

      if (nextList === previousList || _.isEqual(nextList, previousList)) {
        return prev;
      }

      if (markDirty) {
        setHasUserChanges(true);
      }

      return {
        ...prev,
        [listKey]: nextList,
      };
    });
  }, []);

  const markAsDirty = useCallback(() => {
    setHasUserChanges(true);
  }, []);

  return {
    listItems,
    builtConfiguration,
    updateList,
    markAsDirty,
  };
};
